import { Link, useParams } from 'react-router-dom';
import { ArrowLeft, Clock, Calendar, ArrowRight, Target, BookOpen, Loader2 } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { api } from '@/services/api';

// Types for Blog Post
interface BlogPost {
    id: string;
    slug: string;
    title: string;
    excerpt: string;
    content: string;
    read_time?: string; // from API (optional if we transform it)
    readTime: string; // Used in UI
    date: string; // Mapped from created_at
    image?: string; // Optional image
    tags: string[];
    featured: boolean;
    published?: boolean; // Optional in frontend view
}

export default function BlogPage() {
    const { data: blogPosts, isLoading } = useQuery({
        queryKey: ['blog-posts'],
        queryFn: async () => {
            const data = await api.getBlogPosts({ published: true });
            return data.map(p => ({
                ...p,
                readTime: p.read_time,
                date: new Date(p.created_at).toLocaleDateString('vi-VN', { month: 'long', year: 'numeric' }),
            })) as BlogPost[];
        }
    });

    if (isLoading) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-white dark:bg-neutral-900">
                <Loader2 className="w-8 h-8 text-primary-600 animate-spin" />
            </div>
        );
    }

    const posts = blogPosts || [];

    return (
        <div className="min-h-screen bg-white dark:bg-neutral-900">
            {/* Navigation */}
            <nav className="fixed top-0 left-0 right-0 z-50 bg-white/80 dark:bg-neutral-900/80 backdrop-blur-md border-b border-neutral-200/50 dark:border-neutral-800/50">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="flex items-center justify-between h-16">
                        <div className="flex items-center gap-8">
                            <Link to="/" className="flex items-center gap-2">
                                <div className="w-10 h-10 bg-gradient-to-br from-primary-500 via-primary-600 to-secondary-500 rounded-xl flex items-center justify-center shadow-lg shadow-primary-500/30">
                                    <Target className="w-6 h-6 text-white" />
                                </div>
                                <span className="text-xl font-bold bg-gradient-to-r from-primary-600 to-secondary-600 bg-clip-text text-transparent">
                                    VisionBoard
                                </span>
                            </Link>
                        </div>

                        <div className="hidden md:flex items-center gap-8">
                            <Link to="/#features" className="text-sm font-medium text-neutral-600 hover:text-primary-600 transition-colors">Tính năng</Link>
                            <Link to="/blog" className="text-sm font-medium text-primary-600">Blog</Link>
                            <Link to="/docs" className="text-sm font-medium text-neutral-600 hover:text-primary-600 transition-colors">Hướng dẫn</Link>
                        </div>

                        <div className="flex items-center gap-3">
                            <Link to="/login" className="hidden sm:block text-sm font-medium text-neutral-600 hover:text-primary-600 transition-colors">
                                Đăng nhập
                            </Link>
                            <Link to="/register" className="btn-primary text-sm px-5 py-2.5 shadow-lg shadow-primary-500/30">
                                Bắt đầu miễn phí
                            </Link>
                        </div>
                    </div>
                </div>
            </nav>

            {/* Hero Section */}
            <section className="pt-28 pb-16 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-primary-50 via-white to-secondary-50 dark:from-neutral-800 dark:via-neutral-900 dark:to-neutral-800">
                <div className="max-w-4xl mx-auto text-center">
                    <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary-100 dark:bg-primary-600/20 text-primary-600 text-sm font-medium mb-6">
                        <BookOpen className="w-4 h-4" />
                        VisionBoard Blog
                    </div>
                    <h1 className="text-4xl sm:text-5xl font-extrabold text-neutral-900 dark:text-white mb-6">
                        Kiến thức về
                        <span className="block bg-gradient-to-r from-primary-600 to-secondary-600 bg-clip-text text-transparent">
                            OKR, Productivity & Goal Setting
                        </span>
                    </h1>
                    <p className="text-lg text-neutral-600 dark:text-neutral-400 max-w-2xl mx-auto">
                        Những bài viết giúp bạn đặt mục tiêu hiệu quả, quản lý công việc thông minh và đạt được nhiều hơn trong năm 2025.
                    </p>
                </div>
            </section>

            {/* Featured Post */}
            {posts.filter(p => p.featured).map(post => (
                <section key={post.id} className="py-12 px-4 sm:px-6 lg:px-8">
                    <div className="max-w-6xl mx-auto">
                        <div className="bg-gradient-to-r from-primary-600 to-secondary-600 rounded-3xl p-1">
                            <div className="bg-white dark:bg-neutral-800 rounded-3xl p-8 md:p-12">
                                <div className="flex items-center gap-2 mb-4">
                                    <span className="px-3 py-1 bg-primary-100 dark:bg-primary-600/20 text-primary-600 text-xs font-medium rounded-full">
                                        ⭐ Bài viết nổi bật
                                    </span>
                                </div>
                                <h2 className="text-2xl md:text-3xl font-bold text-neutral-900 dark:text-white mb-4">
                                    {post.title}
                                </h2>
                                <p className="text-neutral-600 dark:text-neutral-400 mb-6 text-lg">
                                    {post.excerpt}
                                </p>
                                <div className="flex flex-wrap items-center gap-4 mb-6">
                                    <span className="flex items-center gap-1 text-sm text-neutral-500">
                                        <Clock className="w-4 h-4" />
                                        {post.readTime}
                                    </span>
                                    <span className="flex items-center gap-1 text-sm text-neutral-500">
                                        <Calendar className="w-4 h-4" />
                                        {post.date}
                                    </span>
                                </div>
                                <Link
                                    to={`/blog/${post.slug}`}
                                    className="inline-flex items-center gap-2 text-primary-600 font-medium hover:gap-3 transition-all"
                                >
                                    Đọc bài viết
                                    <ArrowRight className="w-4 h-4" />
                                </Link>
                            </div>
                        </div>
                    </div>
                </section>
            ))}

            {/* All Posts */}
            <section className="py-12 px-4 sm:px-6 lg:px-8 bg-neutral-50 dark:bg-neutral-800/50">
                <div className="max-w-6xl mx-auto">
                    <h2 className="text-2xl font-bold text-neutral-900 dark:text-white mb-8">
                        Tất cả bài viết
                    </h2>
                    {posts.length === 0 ? (
                        <div className="text-center py-12">
                            <p className="text-neutral-500 dark:text-neutral-400">Chưa có bài viết nào.</p>
                        </div>
                    ) : (
                        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
                            {posts.filter(p => !p.featured).map(post => (
                                <article
                                    key={post.id}
                                    className="bg-white dark:bg-neutral-800 rounded-2xl overflow-hidden shadow-lg border border-neutral-200 dark:border-neutral-700 hover:shadow-xl transition-shadow group"
                                >
                                    <div className="h-48 bg-gradient-to-br from-primary-100 to-secondary-100 dark:from-primary-600/20 dark:to-secondary-600/20 flex items-center justify-center">
                                        <BookOpen className="w-16 h-16 text-primary-300 dark:text-primary-600 group-hover:scale-110 transition-transform" />
                                    </div>
                                    <div className="p-6">
                                        <div className="flex flex-wrap gap-2 mb-3">
                                            {post.tags.slice(0, 2).map(tag => (
                                                <span
                                                    key={tag}
                                                    className="px-2 py-1 bg-neutral-100 dark:bg-neutral-700 text-neutral-600 dark:text-neutral-400 text-xs rounded-full"
                                                >
                                                    {tag}
                                                </span>
                                            ))}
                                        </div>
                                        <h3 className="text-lg font-bold text-neutral-900 dark:text-white mb-3 line-clamp-2 group-hover:text-primary-600 transition-colors">
                                            {post.title}
                                        </h3>
                                        <p className="text-neutral-600 dark:text-neutral-400 text-sm mb-4 line-clamp-3">
                                            {post.excerpt}
                                        </p>
                                        <div className="flex items-center justify-between">
                                            <span className="flex items-center gap-1 text-xs text-neutral-500">
                                                <Clock className="w-3 h-3" />
                                                {post.readTime}
                                            </span>
                                            <Link
                                                to={`/blog/${post.slug}`}
                                                className="text-primary-600 text-sm font-medium hover:underline"
                                            >
                                                Đọc tiếp →
                                            </Link>
                                        </div>
                                    </div>
                                </article>
                            ))}
                        </div>
                    )}
                </div>
            </section>

            {/* CTA */}
            <section className="py-16 px-4 sm:px-6 lg:px-8">
                <div className="max-w-4xl mx-auto text-center">
                    <h2 className="text-3xl font-bold text-neutral-900 dark:text-white mb-4">
                        Sẵn sàng thực hành?
                    </h2>
                    <p className="text-lg text-neutral-600 dark:text-neutral-400 mb-8">
                        Đừng chỉ đọc — hãy bắt đầu áp dụng với VisionBoard ngay hôm nay!
                    </p>
                    <Link
                        to="/register"
                        className="btn-primary px-8 py-4 text-lg gap-2 shadow-lg"
                    >
                        Bắt đầu miễn phí
                        <ArrowRight className="w-5 h-5" />
                    </Link>
                </div>
            </section>

            {/* Footer */}
            <footer className="py-8 px-4 sm:px-6 lg:px-8 bg-neutral-100 dark:bg-neutral-800 border-t border-neutral-200 dark:border-neutral-700">
                <div className="max-w-6xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4">
                    <Link to="/" className="flex items-center gap-2">
                        <div className="w-8 h-8 bg-gradient-to-br from-primary-500 to-secondary-500 rounded-lg flex items-center justify-center">
                            <Target className="w-4 h-4 text-white" />
                        </div>
                        <span className="font-bold text-neutral-900 dark:text-white">VisionBoard</span>
                    </Link>
                    <p className="text-sm text-neutral-500">© 2025 VisionBoard. All rights reserved.</p>
                </div>
            </footer>
        </div>
    );
}

// Blog Post Detail Page Component
export function BlogPostPage() {
    const { postId } = useParams(); // postId from route is the slug based on App.tsx route /blog/:slug

    // We treat postId as slug here
    const slug = postId;

    const { data: post, isLoading } = useQuery({
        queryKey: ['blog-post', slug],
        queryFn: async () => {
            if (!slug) return null;
            const data = await api.getBlogPost(slug);
            return {
                ...data,
                readTime: data.read_time,
                date: new Date(data.created_at).toLocaleDateString('vi-VN', { month: 'long', year: 'numeric' }),
                image: undefined,
            } as BlogPost;
        },
        enabled: !!slug,
    });

    const { data: allPosts } = useQuery({
        queryKey: ['blog-posts'],
        queryFn: async () => {
            const data = await api.getBlogPosts({ published: true });
            return data.map(p => ({
                ...p,
                readTime: p.read_time,
                date: new Date(p.created_at).toLocaleDateString('vi-VN', { month: 'long', year: 'numeric' }),
            })) as BlogPost[];
        }
    });

    const otherPosts = allPosts?.filter(p => p.slug !== slug).slice(0, 2) || [];

    // Extract headings for Table of Contents
    const extractHeadings = (content: string) => {
        if (!content) return [];
        const lines = content.split('\n');
        const headings: { level: number; text: string; id: string }[] = [];
        lines.forEach(line => {
            if (line.startsWith('## ')) {
                const text = line.replace('## ', '');
                headings.push({ level: 2, text, id: text.toLowerCase().replace(/\s+/g, '-').replace(/[^\w-]/g, '') });
            } else if (line.startsWith('### ')) {
                const text = line.replace('### ', '');
                headings.push({ level: 3, text, id: text.toLowerCase().replace(/\s+/g, '-').replace(/[^\w-]/g, '') });
            }
        });
        return headings;
    };

    if (isLoading) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-white dark:bg-neutral-900">
                <Loader2 className="w-8 h-8 text-primary-600 animate-spin" />
            </div>
        );
    }

    if (!post) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-neutral-50 dark:bg-neutral-900">
                <div className="text-center">
                    <h1 className="text-2xl font-bold text-neutral-900 dark:text-white mb-4">
                        Bài viết không tìm thấy
                    </h1>
                    <Link to="/blog" className="text-primary-600 hover:underline">
                        ← Quay lại Blog
                    </Link>
                </div>
            </div>
        );
    }

    const headings = extractHeadings(post.content);

    // Render content with better styling
    const renderContent = () => {
        const lines = post.content.split('\n');
        const elements: JSX.Element[] = [];
        let currentList: string[] = [];

        const flushList = (key: number) => {
            if (currentList.length > 0) {
                elements.push(
                    <ul key={`list-${key}`} className="space-y-2 my-4">
                        {currentList.map((item, idx) => (
                            <li key={idx} className="flex items-start gap-3 text-neutral-700 dark:text-neutral-300">
                                <span className="mt-1.5 w-2 h-2 rounded-full bg-primary-500 flex-shrink-0" />
                                <span dangerouslySetInnerHTML={{ __html: item.replace(/\*\*(.*?)\*\*/g, '<strong class="text-neutral-900 dark:text-white">$1</strong>') }} />
                            </li>
                        ))}
                    </ul>
                );
                currentList = [];
            }
        };

        lines.forEach((line, i) => {
            // Headings
            if (line.startsWith('## ')) {
                flushList(i);
                const text = line.replace('## ', '');
                const id = text.toLowerCase().replace(/\s+/g, '-').replace(/[^\w-]/g, '');
                elements.push(
                    <h2 key={i} id={id} className="scroll-mt-24 text-2xl font-bold text-neutral-900 dark:text-white mt-12 mb-6 pb-3 border-b border-neutral-200 dark:border-neutral-700 flex items-center gap-3">
                        <span className="w-1 h-8 bg-gradient-to-b from-primary-500 to-secondary-500 rounded-full" />
                        {text}
                    </h2>
                );
                return;
            }
            if (line.startsWith('### ')) {
                flushList(i);
                const text = line.replace('### ', '');
                const id = text.toLowerCase().replace(/\s+/g, '-').replace(/[^\w-]/g, '');
                elements.push(
                    <h3 key={i} id={id} className="scroll-mt-24 text-xl font-semibold text-neutral-900 dark:text-white mt-8 mb-4 flex items-center gap-2">
                        <span className="text-primary-500">▸</span>
                        {text}
                    </h3>
                );
                return;
            }

            // Blockquotes
            if (line.startsWith('> ')) {
                flushList(i);
                const content = line.replace('> ', '');
                elements.push(
                    <div key={i} className="my-6 relative">
                        <div className="absolute left-0 top-0 bottom-0 w-1 bg-gradient-to-b from-primary-500 to-secondary-500 rounded-full" />
                        <blockquote className="bg-gradient-to-r from-primary-50 to-secondary-50 dark:from-primary-600/10 dark:to-secondary-600/10 rounded-r-xl pl-6 pr-6 py-4 text-neutral-700 dark:text-neutral-300 font-medium border-r border-t border-b border-primary-100 dark:border-primary-600/20">
                            <span dangerouslySetInnerHTML={{ __html: content.replace(/\*\*(.*?)\*\*/g, '<strong class="text-primary-600">$1</strong>') }} />
                        </blockquote>
                    </div>
                );
                return;
            }

            // List items
            if (line.startsWith('- ') || line.startsWith('✅') || line.startsWith('❌') || line.match(/^\d+\./)) {
                let content = line.replace(/^- /, '').replace(/^\d+\.\s*/, '');
                currentList.push(content);
                return;
            }

            // Empty lines
            if (line.trim() === '') {
                flushList(i);
                return;
            }

            // Regular paragraphs
            flushList(i);
            elements.push(
                <p key={i} className="text-neutral-700 dark:text-neutral-300 leading-relaxed mb-4">
                    <span dangerouslySetInnerHTML={{ __html: line.replace(/\*\*(.*?)\*\*/g, '<strong class="text-neutral-900 dark:text-white">$1</strong>') }} />
                </p>
            );
        });

        flushList(lines.length);
        return elements;
    };

    return (
        <div className="min-h-screen bg-white dark:bg-neutral-900">
            {/* Reading Progress Bar */}
            <div className="fixed top-0 left-0 right-0 h-1 z-[60]">
                <div
                    className="h-full bg-gradient-to-r from-primary-500 to-secondary-500 transition-all duration-150"
                    style={{ width: '0%' }}
                    id="reading-progress"
                />
            </div>

            {/* Navigation */}
            <nav className="fixed top-0 left-0 right-0 z-50 bg-white/95 dark:bg-neutral-900/95 backdrop-blur-xl border-b border-neutral-200/50 dark:border-neutral-800/50">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="flex items-center justify-between h-16">
                        <Link to="/blog" className="flex items-center gap-2 text-neutral-600 hover:text-primary-600 transition-colors">
                            <ArrowLeft className="w-5 h-5" />
                            <span className="hidden sm:inline">Quay lại Blog</span>
                        </Link>
                        <div className="flex items-center gap-3">
                            <Link to="/register" className="btn-primary text-sm px-5 py-2.5">
                                Bắt đầu miễn phí
                            </Link>
                        </div>
                    </div>
                </div>
            </nav>

            <div className="pt-20 pb-16">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="flex gap-8">
                        {/* Table of Contents - Sticky Sidebar */}
                        <aside className="hidden lg:block w-64 flex-shrink-0">
                            <div className="sticky top-24">
                                <div className="bg-neutral-50 dark:bg-neutral-800/50 rounded-2xl p-6 border border-neutral-200 dark:border-neutral-700">
                                    <h4 className="font-bold text-neutral-900 dark:text-white mb-4 flex items-center gap-2">
                                        <BookOpen className="w-4 h-4 text-primary-500" />
                                        Nội dung bài viết
                                    </h4>
                                    <nav className="space-y-2">
                                        {headings.map((heading, idx) => (
                                            <a
                                                key={idx}
                                                href={`#${heading.id}`}
                                                className={`block text-sm transition-colors hover:text-primary-600 ${heading.level === 2
                                                    ? 'text-neutral-700 dark:text-neutral-300 font-medium'
                                                    : 'text-neutral-500 dark:text-neutral-400 pl-3 border-l-2 border-neutral-200 dark:border-neutral-600'
                                                    }`}
                                            >
                                                {heading.text.length > 30 ? heading.text.substring(0, 30) + '...' : heading.text}
                                            </a>
                                        ))}
                                    </nav>
                                </div>

                                {/* Quick Stats */}
                                <div className="mt-4 bg-gradient-to-br from-primary-50 to-secondary-50 dark:from-primary-600/10 dark:to-secondary-600/10 rounded-2xl p-6 border border-primary-100 dark:border-primary-600/20">
                                    <div className="flex items-center gap-3 mb-3">
                                        <Clock className="w-5 h-5 text-primary-500" />
                                        <div>
                                            <p className="text-xs text-neutral-500">Thời gian đọc</p>
                                            <p className="font-semibold text-neutral-900 dark:text-white">{post.readTime}</p>
                                        </div>
                                    </div>
                                    <div className="flex items-center gap-3">
                                        <Calendar className="w-5 h-5 text-primary-500" />
                                        <div>
                                            <p className="text-xs text-neutral-500">Cập nhật</p>
                                            <p className="font-semibold text-neutral-900 dark:text-white">{post.date}</p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </aside>

                        {/* Main Content */}
                        <article className="flex-1 min-w-0">
                            <header className="mb-10">
                                <div className="flex flex-wrap gap-2 mb-4">
                                    {post.tags.map(tag => (
                                        <span
                                            key={tag}
                                            className="px-4 py-1.5 bg-gradient-to-r from-primary-100 to-secondary-100 dark:from-primary-600/20 dark:to-secondary-600/20 text-primary-700 dark:text-primary-400 text-sm font-medium rounded-full"
                                        >
                                            {tag}
                                        </span>
                                    ))}
                                </div>
                                <h1 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-neutral-900 dark:text-white mb-6 leading-tight">
                                    {post.title}
                                </h1>
                                <p className="text-xl text-neutral-600 dark:text-neutral-400 leading-relaxed">
                                    {post.excerpt}
                                </p>

                                {/* Mobile: TOC & Stats */}
                                <div className="lg:hidden mt-6 p-4 bg-neutral-50 dark:bg-neutral-800/50 rounded-xl">
                                    <div className="flex items-center gap-4 text-sm text-neutral-500 mb-4">
                                        <span className="flex items-center gap-1">
                                            <Clock className="w-4 h-4" />
                                            {post.readTime}
                                        </span>
                                        <span className="flex items-center gap-1">
                                            <Calendar className="w-4 h-4" />
                                            {post.date}
                                        </span>
                                    </div>
                                    <details className="group">
                                        <summary className="cursor-pointer text-primary-600 font-medium flex items-center gap-2">
                                            <BookOpen className="w-4 h-4" />
                                            Xem mục lục
                                            <span className="group-open:rotate-180 transition-transform">▼</span>
                                        </summary>
                                        <nav className="mt-3 space-y-2 pl-6">
                                            {headings.filter(h => h.level === 2).map((heading, idx) => (
                                                <a
                                                    key={idx}
                                                    href={`#${heading.id}`}
                                                    className="block text-sm text-neutral-600 dark:text-neutral-400 hover:text-primary-600"
                                                >
                                                    {heading.text}
                                                </a>
                                            ))}
                                        </nav>
                                    </details>
                                </div>
                            </header>

                            {/* Hero Image Placeholder */}
                            <div className="mb-10 bg-gradient-to-br from-primary-100 via-secondary-50 to-primary-100 dark:from-primary-600/20 dark:to-secondary-600/20 rounded-2xl h-64 flex items-center justify-center">
                                <div className="text-center">
                                    <Target className="w-16 h-16 text-primary-300 dark:text-primary-600 mx-auto mb-3" />
                                    <p className="text-primary-400 dark:text-primary-500 font-medium">VisionBoard Blog</p>
                                </div>
                            </div>

                            {/* Content */}
                            <div className="prose prose-lg dark:prose-invert max-w-none">
                                {renderContent()}
                            </div>

                            {/* CTA Box */}
                            <div className="mt-16 relative overflow-hidden">
                                <div className="absolute inset-0 bg-gradient-to-r from-primary-600 to-secondary-600 rounded-3xl" />
                                <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxwYXRoIGQ9Ik0zNiAxOGMtOS45NDEgMC0xOCA4LjA1OS0xOCAxOHs4LjA1OSAxOCAxOCAxOCAxOC04LjA1OSAxOC0xOC04LjA1OS0xOC0xOHptMCAzMmMtNy43MzIgMC0xNC02LjI2OC0xNC0xNHM2LjI2OC0xNCAxNC0xNCAxNCA2LjI2OCAxNCAxNC02LjI2OCAxNC0xNCAxNHoiIGZpbGw9IiNmZmYiIGZpbGwtb3BhY2l0eT0iLjA1Ii8+PC9nPjwvc3ZnPg==')] opacity-30 rounded-3xl" />
                                <div className="relative p-8 sm:p-12 text-center">
                                    <h3 className="text-2xl sm:text-3xl font-bold text-white mb-4">
                                        🚀 Sẵn sàng áp dụng ngay?
                                    </h3>
                                    <p className="text-white/80 mb-8 max-w-lg mx-auto">
                                        VisionBoard giúp bạn biến những kiến thức này thành hành động cụ thể. Hoàn toàn miễn phí!
                                    </p>
                                    <Link
                                        to="/register"
                                        className="inline-flex items-center gap-2 px-8 py-4 bg-white text-primary-600 font-bold rounded-xl hover:bg-neutral-100 transition-all shadow-lg text-lg"
                                    >
                                        Bắt đầu với VisionBoard
                                        <ArrowRight className="w-5 h-5" />
                                    </Link>
                                </div>
                            </div>

                            {/* Related Posts */}
                            {otherPosts.length > 0 && (
                                <div className="mt-16">
                                    <h3 className="text-2xl font-bold text-neutral-900 dark:text-white mb-8">
                                        📚 Bài viết liên quan
                                    </h3>
                                    <div className="grid sm:grid-cols-2 gap-6">
                                        {otherPosts.map(relatedPost => (
                                            <Link
                                                key={relatedPost.id}
                                                to={`/blog/${relatedPost.slug}`}
                                                className="group bg-neutral-50 dark:bg-neutral-800/50 rounded-2xl p-6 border border-neutral-200 dark:border-neutral-700 hover:border-primary-300 dark:hover:border-primary-600 transition-all hover:-translate-y-1"
                                            >
                                                <div className="flex flex-wrap gap-2 mb-3">
                                                    {relatedPost.tags.slice(0, 2).map(tag => (
                                                        <span key={tag} className="px-2 py-1 bg-neutral-100 dark:bg-neutral-700 text-neutral-500 text-xs rounded-full">
                                                            {tag}
                                                        </span>
                                                    ))}
                                                </div>
                                                <h4 className="font-bold text-neutral-900 dark:text-white group-hover:text-primary-600 transition-colors mb-2 line-clamp-2">
                                                    {relatedPost.title}
                                                </h4>
                                                <p className="text-sm text-neutral-500 flex items-center gap-1">
                                                    <Clock className="w-3 h-3" />
                                                    {relatedPost.readTime}
                                                </p>
                                            </Link>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </article>
                    </div>
                </div>
            </div>

            {/* Footer */}
            <footer className="py-8 px-4 sm:px-6 lg:px-8 bg-neutral-100 dark:bg-neutral-800 border-t border-neutral-200 dark:border-neutral-700">
                <div className="max-w-6xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4">
                    <Link to="/" className="flex items-center gap-2">
                        <div className="w-8 h-8 bg-gradient-to-br from-primary-500 to-secondary-500 rounded-lg flex items-center justify-center">
                            <Target className="w-4 h-4 text-white" />
                        </div>
                        <span className="font-bold text-neutral-900 dark:text-white">VisionBoard</span>
                    </Link>
                    <div className="flex items-center gap-4">
                        <Link to="/blog" className="text-sm text-neutral-500 hover:text-primary-600">Blog</Link>
                        <Link to="/docs" className="text-sm text-neutral-500 hover:text-primary-600">Hướng dẫn</Link>
                        <span className="text-sm text-neutral-500">© 2025 VisionBoard. All rights reserved.</span>
                    </div>
                </div>
            </footer>

            {/* Reading Progress Script */}
            <script dangerouslySetInnerHTML={{
                __html: `
                window.addEventListener('scroll', function() {
                    var winScroll = document.body.scrollTop || document.documentElement.scrollTop;
                    var height = document.documentElement.scrollHeight - document.documentElement.clientHeight;
                    var scrolled = (winScroll / height) * 100;
                    var progressBar = document.getElementById('reading-progress');
                    if (progressBar) progressBar.style.width = scrolled + '%';
                });
            `}} />
        </div>
    );
}
